import { JSX } from 'react';
import { Avatar, Paper, Step, StepLabel, Stepper, Theme } from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import { Candidate, Job } from 'types/types';
import { AnalysisState } from './jobAnalysisTypes';
import {
  AnalysisStepContent,
  CandidateStepContent,
  JobStepContent,
  ResumeStepContent,
} from './JobAnalysisStepContents';

interface JobAnalysisStepperProps {
  activeStep: number;
  isMobile: boolean;
  selectedCandidate: Candidate | null;
  selectedJob: Job | null;
  analysisState: AnalysisState;
  theme: Theme;
  moveToStep: (step: number) => void;
  savedResumeId: string | null;
}

function JobAnalysisStepper(props: JobAnalysisStepperProps): JSX.Element {
  const avatarBgColor = (step: number): string =>
    props.activeStep >= step ? props.theme.palette.primary.main : props.theme.palette.grey[300];

  return (
    <Paper
      elevation={4}
      sx={{
        fontSize: props.isMobile ? '0.875rem' : '1rem',
        display: 'flex',
        position: 'relative',
        m: 0,
        borderRadius: 0,
        mb: 1,
        p: 0,
        gap: 1,
        maxWidth: '100%',
        width: '100%',
        flexDirection: 'column',
        '& .MuiAvatar-root': props.isMobile ? { width: 32, height: 32 } : {},
        '& .MuiStepLabel-label': props.isMobile ? { fontSize: '0.9rem', marginTop: '4px' } : {},
        '& .MuiSvgIcon-root': props.isMobile ? { width: '0.9rem', height: '0.9rem' } : {},
      }}
    >
      <Stepper
        activeStep={props.activeStep}
        alternativeLabel
        sx={{
          mt: 1,
          mb: 1,
          fontWeight: 'bold',
          '& .MuiStepLabel-label': {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minWidth: 0,
          },
          '& .MuiStep-root': { minWidth: 0, px: props.isMobile ? 0.25 : 1 },
          '& .MuiStepLabel-root': { minWidth: 0 },
          '& .MuiStepLabel-labelContainer': { minWidth: 0 },
          '& .MuiStepConnector-root': props.isMobile
            ? { marginLeft: '-8px', marginRight: '-8px' }
            : {},
          maxWidth: '100%',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Step key={0} sx={{ minWidth: 0 }}>
          <StepLabel
            sx={{ cursor: 'pointer' }}
            onClick={() => props.moveToStep(0)}
            slots={{
              stepIcon: (): JSX.Element => (
                <Avatar sx={{ bgcolor: avatarBgColor(0), color: 'white' }}>
                  <PersonIcon />
                </Avatar>
              ),
            }}
          >
            <CandidateStepContent
              isMobile={props.isMobile}
              selectedCandidate={props.selectedCandidate}
            />
          </StepLabel>
        </Step>
        <Step key={1} sx={{ minWidth: 0 }}>
          <StepLabel
            sx={{ cursor: 'pointer' }}
            onClick={() => props.moveToStep(1)}
            slots={{
              stepIcon: (): JSX.Element => (
                <Avatar sx={{ bgcolor: avatarBgColor(1), color: 'white' }}>
                  <WorkIcon />
                </Avatar>
              ),
            }}
          >
            <JobStepContent isMobile={props.isMobile} selectedJob={props.selectedJob} />
          </StepLabel>
        </Step>
        <Step key={2} sx={{ minWidth: 0 }}>
          <StepLabel
            sx={{ cursor: 'pointer' }}
            onClick={() => props.moveToStep(2)}
            slots={{
              stepIcon: (): JSX.Element => (
                <Avatar sx={{ bgcolor: avatarBgColor(2), color: 'white' }}>
                  <AutoAwesomeIcon />
                </Avatar>
              ),
            }}
          >
            <AnalysisStepContent isMobile={props.isMobile} analysisState={props.analysisState} />
          </StepLabel>
        </Step>
        <Step key={3} sx={{ minWidth: 0 }}>
          <StepLabel
            sx={{ cursor: 'pointer' }}
            onClick={() => props.moveToStep(3)}
            slots={{
              stepIcon: (): JSX.Element => (
                <Avatar sx={{ bgcolor: avatarBgColor(3), color: 'white' }}>
                  <AssessmentIcon />
                </Avatar>
              ),
            }}
          >
            <ResumeStepContent
              isMobile={props.isMobile}
              savedResumeId={props.savedResumeId}
              theme={props.theme}
            />
          </StepLabel>
        </Step>
      </Stepper>
    </Paper>
  );
}

export { JobAnalysisStepper };
